import { useEffect } from 'react';

import { useColorSchemeUserSettingLink } from '../contexts/color-scheme-context';
import { useSession } from '../contexts/session-context';
import { useUserSetting } from '../hooks/use-user-setting';
import { COLOR_SCHEME_SETTING } from '../util/color-scheme-setting';

/**
 * Joins the colour scheme to the signed-in user's server-side setting (UX-930). Renders nothing.
 * Mounted once per shell, inside `SessionProvider` (it needs the user) and so below
 * `ColorSchemeProvider`, which it reaches through `useColorSchemeUserSettingLink`.
 *
 * - SIGNED OUT (the login page, an anonymous delivery visitor): does nothing. `useUserSetting`
 *   makes no request without a user, and the device-level key alone drives the scheme.
 * - SIGNED IN, DOCUMENT NOT LOADED YET (or the request failed): still the device-level key. It is
 *   what painted before React mounted, so nothing changes on screen while the `GET` is out.
 * - DOCUMENT LOADED: the user's stored choice is adopted and the device-level key rewritten to
 *   match. When the two already agree, which is every ordinary reload, that is a no-op. When they
 *   differ (the choice was changed in another browser, or another user last used this one) the
 *   page switches once, and the next load here paints right first time.
 * - NO STORED CHOICE: the store's carry-over sends the device-level key up, if this browser has
 *   one (see `color-scheme-setting.ts` for the Login As exception); otherwise the device-level key
 *   keeps driving.
 *
 * A change made through `setPreference` is applied and written to the device-level key by the
 * provider at once, and sent to the server through the writer registered here. A failed save is
 * silent (the store's rule) and the device-level key still holds the choice.
 *
 * Under Login As the session user is the effective user, so it is the customer's choice that is
 * adopted and the customer's that a change is saved to.
 */
export function ColorSchemeUserSettingSync() {
    const link = useColorSchemeUserSettingLink();
    const { user } = useSession();
    const { value, setValue, status } = useUserSetting(COLOR_SCHEME_SETTING);
    const isSignedIn = user?.userId !== undefined && user?.userId !== null;

    useEffect(() => {
        if (!link || !isSignedIn) return;
        link.setUserPreferenceWriter(setValue);
        return () => link.setUserPreferenceWriter(null);
    }, [link, isSignedIn, setValue]);

    useEffect(() => {
        if (!link || !isSignedIn || status !== 'loaded' || value === undefined) return;
        link.adoptUserPreference(value);
    }, [link, isSignedIn, status, value]);

    return null;
}
